import React, { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { COLORS, FONTSIZE } from "helpers/designConstants";

import ProductGroupCard from "./ProductGroupCard";

/**
 * Flat, relevance-ordered grid — one card per articleNumber. No season headers: each card carries
 * its own season chips, which is true per item in a way a shared header cannot be.
 *
 * While a newer search is in flight the current results are dimmed and non-interactive, so a rep
 * cannot click a card that is about to be replaced by a different result set.
 */
const SearchResultsPanel = ({ groups, isSearching, isTruncated, ...cardProps }) => {
  const { t } = useTranslation();
  const scrollRef = useRef();

  // Reset to the top whenever a new result set is accepted. The preview's top-40-by-relevance is
  // not a prefix of the full result set, so keeping a scroll offset across the 2->3 character
  // transition would drop the rep into an unrelated position in a different list.
  useEffect(() => {
    if (scrollRef.current != null) scrollRef.current.scrollTop = 0;
  }, [groups]);

  return (
    <Scroll ref={scrollRef}>
      <Grid isSearching={isSearching}>
        {(groups || []).map((group) => (
          <ProductGroupCard key={group.articleNumber} group={group} {...cardProps} />
        ))}
      </Grid>
      {isTruncated && groups?.length > 0 && (
        <EndMarker>
          {t(
            "search_end_marker",
            "End of the first 250 matches — narrow your search to see more."
          )}
        </EndMarker>
      )}
    </Scroll>
  );
};

const Scroll = styled.div`
  overflow-y: auto;
`;

const Grid = styled.div`
  padding: 5px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 0.33333fr));
  grid-gap: 3px;
  align-content: start;
  opacity: ${({ isSearching }) => (isSearching ? 0.45 : 1)};
  pointer-events: ${({ isSearching }) => (isSearching ? "none" : "auto")};
  transition: opacity 0.15s ease-in-out;
`;

const EndMarker = styled.div`
  padding: 12px;
  text-align: center;
  font-size: ${FONTSIZE.xsmall};
  color: ${COLORS.alertOrange};
  text-transform: uppercase;
`;

export default SearchResultsPanel;
